const RELOAD_STAMP_KEY = 'vs:chunk-reload-at';
// Safety net against a reload loop while index.html and its chunks disagree for a moment
// (deploy still propagating). The build comparison below is the main loop guard.
const RELOAD_COOLDOWN_MS = 10_000;
// The error screen is held during the check: on a gym Wi-Fi without internet the request
// can hang for minutes, so give up early and show it.
const INDEX_CHECK_TIMEOUT_MS = 3_000;
// A reload normally unloads the page well before this. Still alive afterwards means the
// navigation was cancelled (beforeunload prompt): stop waiting and show the error screen.
const RELOAD_GIVE_UP_MS = 10_000;

export interface StaleChunkContext {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  now: number;
  online: boolean;
  /** Entry script of the running build, e.g. `/assets/index-abc123.js`. */
  currentEntry: string | null;
  fetchIndexHtml: () => Promise<string>;
  timeoutMs?: number;
}

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timed out after ${ms} ms`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error: unknown) => { clearTimeout(timer); reject(error); },
    );
  });

/**
 * After a deploy, a tab still running the previous build asks for chunk hashes that
 * no longer exist on Amplify. A full reload fetches the new index.html and its chunks.
 * Resolves to true when a reload was triggered.
 */
export async function reloadOnStaleChunk(ctx: StaleChunkContext): Promise<boolean> {
  // Offline the import failed for lack of network, not a stale build: reloading would
  // replace a working in-memory app (live entry included) with the browser's offline page.
  if (!ctx.online) return false;

  let last = 0;
  try {
    last = Number(ctx.storage.getItem(RELOAD_STAMP_KEY) ?? 0);
  } catch {
    // Storage blocked: rely on the build comparison alone.
  }
  if (Number.isFinite(last) && ctx.now - last < RELOAD_COOLDOWN_MS) return false;

  let html: string;
  try {
    html = await withTimeout(ctx.fetchIndexHtml(), ctx.timeoutMs ?? INDEX_CHECK_TIMEOUT_MS);
  } catch {
    // navigator.onLine is true on a gym Wi-Fi without internet: only reload once the
    // server has actually answered.
    return false;
  }
  // The server still serves the running build: the chunk is genuinely missing and a
  // reload would only loop.
  if (ctx.currentEntry && html.includes(ctx.currentEntry)) return false;

  try {
    ctx.storage.setItem(RELOAD_STAMP_KEY, String(ctx.now));
  } catch {
    // Without storage the build comparison still prevents a loop.
  }
  ctx.reload();
  return true;
}

let lastCheck: Promise<boolean> | null = null;
let lastSettled: Promise<void> | null = null;
let checkInFlight = false;
let reloading = false;

/** Resolves to true when the latest stale-chunk check ended in a reload; null if none ran. */
export function staleChunkReloadCheck(): Promise<boolean> | null {
  return lastCheck;
}

/**
 * Resolves once the page is known to stay as it is and the error screen should show:
 * no reload was triggered, or the reload did not unload the page in time.
 */
export function staleChunkReloadSettled(): Promise<void> | null {
  return lastSettled;
}

// Chrome, Firefox and Safari wordings of a failed dynamic import, plus Vite's own error
// when a dependency's stylesheet is gone.
const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS for',
];

interface SentryLikeEvent {
  exception?: { values?: { value?: string }[] };
}

/**
 * Sentry `beforeSend`: drops only the failed-import error, and only when it led to a
 * reload. Any other error is reported, even while the page is reloading.
 */
export async function dropStaleChunkEvent<E extends SentryLikeEvent>(event: E): Promise<E | null> {
  const messages = event.exception?.values?.map((v) => v.value ?? '') ?? [];
  const isStaleChunk = messages.some((m) => STALE_CHUNK_MESSAGES.some((s) => m.includes(s)));
  if (!isStaleChunk) return event;
  return (await staleChunkReloadCheck()) ? null : event;
}

/** True while a stale-chunk check runs or the page is reloading because of one. */
export function isStaleChunkReloadInProgress(): boolean {
  return checkInFlight || reloading;
}

const fetchIndexHtml = async (): Promise<string> => {
  const response = await fetch('/', {
    cache: 'no-store',
    signal: AbortSignal.timeout(INDEX_CHECK_TIMEOUT_MS),
  });
  if (!response.ok) throw new Error(`index.html: HTTP ${response.status}`);
  return response.text();
};

const currentEntryScript = (): string | null =>
  document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute('src') ?? null;

export function installStaleChunkReload(
  overrides: Partial<Pick<StaleChunkContext, 'reload' | 'fetchIndexHtml'>> = {},
): () => void {
  // No preventDefault: with it Vite resolves the import to undefined, and every
  // `lazy(() => import(...).then((m) => ...))` would crash on a misleading TypeError.
  const onPreloadError = (): void => {
    // One failed chunk fires the event for each missing dependency: check once.
    if (checkInFlight || reloading) return;
    checkInFlight = true;
    lastCheck = reloadOnStaleChunk({
      storage: window.sessionStorage,
      reload: overrides.reload ?? (() => window.location.reload()),
      now: Date.now(),
      online: navigator.onLine,
      currentEntry: currentEntryScript(),
      fetchIndexHtml: overrides.fetchIndexHtml ?? fetchIndexHtml,
    })
      .catch(() => false)
      .then((reloaded) => {
        checkInFlight = false;
        reloading = reloaded;
        return reloaded;
      });
    lastSettled = lastCheck.then((reloaded) => {
      if (!reloaded) return undefined;
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          reloading = false;
          resolve();
        }, RELOAD_GIVE_UP_MS);
      });
    });
  };
  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}

/** Test-only: forget the module-level check state. */
export function resetStaleChunkReloadForTests(): void {
  lastCheck = null;
  lastSettled = null;
  checkInFlight = false;
  reloading = false;
}
